import { useState, useRef, type DragEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { systemPrompts, LANGUAGE_LABELS } from '@/api/systemPrompts';
import type {
  SystemPromptRow,
  SystemPromptTranslationRow,
  SystemPromptOverrideRow,
  PromptLanguage,
} from '@/api/systemPrompts';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Play, Save, Trash2, Loader2, Upload, X, Mic2 } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useAuth } from '@/lib/auth-store';

const LANGS: PromptLanguage[] = ['en', 'he', 'yi'];

const ACCEPTED_MIMES = [
  'audio/wav', 'audio/x-wav', 'audio/mpeg', 'audio/mp3',
  'audio/ogg', 'audio/webm', 'audio/mp4', 'audio/x-m4a',
];
const MAX_BYTES = 25 * 1024 * 1024; // 25 MB — matches backend

// ─────────────────────────────────────────────────────────────
// Audio upload sub-widget
// ─────────────────────────────────────────────────────────────
// Compact drop zone that lives inside the override row. Uses the same
// MIME allow-list + 25 MB limit as the backend multer config so the user
// gets feedback before hitting the server.

interface AudioUploadProps {
  prompt: SystemPromptRow;
  lang: PromptLanguage;
  override: SystemPromptOverrideRow | undefined;
}

function AudioUpload({ prompt, lang, override }: AudioUploadProps) {
  const qc = useQueryClient();
  const accessToken = useAuth((s) => s.accessToken);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const hasAudio =
    Boolean(override?.audio_s3_key) && Boolean(override?.audio_s3_bucket);

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      return systemPrompts.uploadOverrideAudio(prompt.id, lang, fd);
    },
    onSuccess: () => {
      setUploadError(null);
      qc.invalidateQueries({ queryKey: ['systemPrompt', prompt.id] });
    },
    onError: (err: unknown) => {
      setUploadError(
        err instanceof Error && err.message ? err.message : 'Upload failed.',
      );
    },
  });

  const deleteAudioMutation = useMutation({
    mutationFn: () => {
      if (!override) throw new Error('no_override');
      return systemPrompts.deleteOverrideAudio(prompt.id, override.id);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['systemPrompt', prompt.id] });
    },
  });

  function validateAndUpload(file: File | null) {
    setUploadError(null);
    if (!file) return;
    if (!ACCEPTED_MIMES.includes(file.type)) {
      setUploadError(`Unsupported file type (${file.type || 'unknown'}).`);
      return;
    }
    if (file.size > MAX_BYTES) {
      setUploadError(`File exceeds 25 MB limit (${(file.size / 1024 / 1024).toFixed(1)} MB).`);
      return;
    }
    uploadMutation.mutate(file);
  }

  function handleDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0] ?? null;
    validateAndUpload(f);
  }

  const audioUrl =
    hasAudio && override
      ? systemPrompts.overrideAudioUrl(prompt.id, override.id, accessToken)
      : null;

  // With an audio override already present: mini player + replace/remove.
  if (hasAudio && audioUrl) {
    return (
      <div className="flex items-center gap-2 flex-wrap">
        <Badge className="bg-violet-100 text-violet-700 hover:bg-violet-100 border-violet-200 text-[10px] font-medium gap-1">
          <Mic2 className="h-3 w-3" />
          Custom audio
        </Badge>
        <audio
          controls
          src={audioUrl}
          className="h-7 max-w-[220px]"
          preload="none"
        />
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="outline"
            className="h-7 px-2 text-xs gap-1"
            disabled={uploadMutation.isPending}
            onClick={() => fileInputRef.current?.click()}
            title="Replace uploaded audio"
          >
            {uploadMutation.isPending
              ? <Loader2 className="h-3 w-3 animate-spin" />
              : <Upload className="h-3 w-3" />}
            Replace
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 gap-1"
            disabled={deleteAudioMutation.isPending}
            onClick={() => {
              if (confirm(`Remove custom audio for ${LANGUAGE_LABELS[lang]}? TTS fallback will apply.`)) {
                deleteAudioMutation.mutate();
              }
            }}
            title="Remove audio, revert to TTS"
          >
            {deleteAudioMutation.isPending
              ? <Loader2 className="h-3 w-3 animate-spin" />
              : <X className="h-3 w-3" />}
            Remove
          </Button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_MIMES.join(',')}
          className="sr-only"
          onChange={(e) => validateAndUpload(e.target.files?.[0] ?? null)}
          aria-label={`Replace ${LANGUAGE_LABELS[lang]} custom audio`}
        />
        {uploadError && (
          <p role="alert" className="w-full text-[11px] text-red-500">{uploadError}</p>
        )}
      </div>
    );
  }

  // No audio yet: compact drop zone.
  return (
    <div className="flex flex-col gap-1">
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        role="button"
        tabIndex={0}
        aria-label={`Upload custom audio for ${LANGUAGE_LABELS[lang]}`}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click();
        }}
        className={cn(
          'flex items-center gap-2 rounded border border-dashed px-3 py-1.5 cursor-pointer text-xs transition-colors select-none',
          uploadMutation.isPending
            ? 'border-slate-300 bg-slate-100 cursor-wait'
            : dragOver
              ? 'border-violet-500 bg-violet-50 text-violet-700'
              : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:text-slate-700',
        )}
      >
        {uploadMutation.isPending
          ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
          : <Upload className="h-3.5 w-3.5" />}
        <span>
          {uploadMutation.isPending
            ? 'Uploading…'
            : 'Drop audio or click to upload — uses TTS if no file'}
        </span>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_MIMES.join(',')}
        className="sr-only"
        onChange={(e) => validateAndUpload(e.target.files?.[0] ?? null)}
        aria-label={`Upload ${LANGUAGE_LABELS[lang]} audio`}
      />
      {uploadError && (
        <p role="alert" className="text-[11px] text-red-500">{uploadError}</p>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Per-language override row
// ─────────────────────────────────────────────────────────────

interface OverrideRowProps {
  prompt: SystemPromptRow;
  lang: PromptLanguage;
  override: SystemPromptOverrideRow | undefined;
  baseline: SystemPromptTranslationRow | undefined;
}

function OverrideRow({ prompt, lang, override, baseline }: OverrideRowProps) {
  const qc = useQueryClient();
  const [text, setText] = useState(
    override?.tts_text ?? '',
  );
  const [dirty, setDirty] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  const saveMutation = useMutation({
    mutationFn: () =>
      systemPrompts.upsertOverride(prompt.id, {
        language: lang,
        tts_text: text.trim() ? text : null,
      }),
    onSuccess: () => {
      setDirty(false);
      qc.invalidateQueries({ queryKey: ['systemPrompt', prompt.id] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => {
      if (!override) throw new Error('no_override');
      return systemPrompts.deleteOverride(prompt.id, override.id);
    },
    onSuccess: () => {
      setText('');
      setDirty(false);
      qc.invalidateQueries({ queryKey: ['systemPrompt', prompt.id] });
    },
  });

  const previewMutation = useMutation({
    mutationFn: () => systemPrompts.preview(prompt.id, lang, text || undefined),
    onSuccess: (data) => {
      setPreviewUrl(data.url);
      setTimeout(() => audioRef.current?.play(), 100);
    },
  });

  const isRtl = lang === 'he' || lang === 'yi';
  const baselineText = baseline?.tts_text;
  const hasAudio = Boolean(override?.audio_s3_key) && Boolean(override?.audio_s3_bucket);

  return (
    <div className="grid grid-cols-[80px_1fr_auto] gap-2 items-start py-2 border-b border-slate-100 last:border-0">
      <div className="pt-2">
        <span className={cn(
          'block text-xs font-semibold',
          (lang === 'he' || lang === 'yi') ? 'text-amber-600' : 'text-slate-500',
        )}>
          {LANGUAGE_LABELS[lang]}
        </span>
        {override && (
          <Badge variant="outline" className="mt-1 text-[9px] font-normal">
            overriding
          </Badge>
        )}
      </div>

      <div className="space-y-2">
        <Textarea
          value={text}
          onChange={(e) => { setText(e.target.value); setDirty(true); }}
          rows={2}
          dir={isRtl ? 'rtl' : 'ltr'}
          className={cn(
            'text-sm resize-none',
            hasAudio && 'opacity-60',
          )}
          placeholder={baselineText
            ? `Override (default: "${baselineText.slice(0, 80)}${baselineText.length > 80 ? '…' : ''}")`
            : 'Enter override text…'
          }
          aria-describedby={hasAudio ? `${prompt.id}-${lang}-audio-note` : undefined}
        />
        {hasAudio && (
          <p
            id={`${prompt.id}-${lang}-audio-note`}
            className="text-[10px] text-violet-600"
          >
            Custom audio takes precedence over TTS text.
          </p>
        )}

        <AudioUpload prompt={prompt} lang={lang} override={override} />

        {override && !dirty && !hasAudio && (
          <p className="text-[10px] text-slate-400">
            Updated {new Date(override.updated_at).toLocaleString()}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-1.5 pt-0.5">
        <Button
          size="sm"
          variant="outline"
          className="h-7 px-2"
          disabled={!text.trim() || previewMutation.isPending}
          onClick={() => previewMutation.mutate()}
          title="Preview TTS"
        >
          {previewMutation.isPending
            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
            : <Play className="h-3.5 w-3.5" />}
        </Button>
        <Button
          size="sm"
          className="h-7 px-2"
          disabled={!dirty || saveMutation.isPending}
          onClick={() => saveMutation.mutate()}
          title="Save override"
        >
          {saveMutation.isPending
            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
            : <Save className="h-3.5 w-3.5" />}
        </Button>
        {override && (
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
            disabled={deleteMutation.isPending}
            onClick={() => {
              if (confirm(`Delete ${LANGUAGE_LABELS[lang]} override? Global default will apply.`)) {
                deleteMutation.mutate();
              }
            }}
            title="Delete override (fall back to global)"
          >
            {deleteMutation.isPending
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <Trash2 className="h-3.5 w-3.5" />}
          </Button>
        )}
      </div>

      {previewUrl && (
        <audio ref={audioRef} src={previewUrl} className="hidden" onEnded={() => setPreviewUrl(null)} />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Editor (top-level export)
// ─────────────────────────────────────────────────────────────

interface Props {
  prompt: SystemPromptRow;
  overrides: SystemPromptOverrideRow[];
  translations: SystemPromptTranslationRow[];
  isSuperAdmin: boolean;
}

export function PromptOverrideEditor({
  prompt, overrides, translations, isSuperAdmin,
}: Props) {
  const translationByLang = Object.fromEntries(
    translations.map((t) => [t.language, t]),
  ) as Record<PromptLanguage, SystemPromptTranslationRow>;

  // For a given language, show the FIRST matching override for the viewer's
  // scope. Reseller users see reseller_id overrides, customer users see
  // customer_id overrides; the API already filters so we can just pick per
  // language.
  const overrideByLang = Object.fromEntries(
    overrides
      .filter((o) => !o.ivr_node_id) // node-level overrides shown separately if we add an editor for them
      .map((o) => [o.language, o]),
  ) as Record<PromptLanguage, SystemPromptOverrideRow | undefined>;

  if (isSuperAdmin) {
    // Super admin view: read-only summary + link to go edit defaults / node overrides
    if (overrides.length === 0) {
      return (
        <p className="text-xs text-slate-500">
          No overrides exist for this prompt. Resellers and customers can
          create overrides from their own portal.
        </p>
      );
    }
    return (
      <div className="space-y-2">
        <p className="text-xs text-slate-500">
          Overrides set by resellers, customers, or specific IVR nodes.
          Super admin should edit the global defaults in the other tab.
        </p>
        <div className="rounded border border-slate-200 divide-y divide-slate-100 bg-white">
          {overrides.map((o) => {
            const hasCustomAudio = Boolean(o.audio_s3_key) && Boolean(o.audio_s3_bucket);
            return (
              <div key={o.id} className="px-3 py-2 text-xs flex items-start gap-3">
                <span className="font-semibold text-slate-700 w-16 shrink-0">
                  {LANGUAGE_LABELS[o.language]}
                </span>
                <span className="text-slate-500 w-24 shrink-0">
                  {o.ivr_node_id
                    ? 'Node'
                    : o.customer_id
                      ? 'Customer'
                      : o.reseller_id
                        ? 'Reseller'
                        : 'Global'}
                </span>
                <span className="flex-1 text-slate-900 flex items-center gap-2 flex-wrap">
                  {hasCustomAudio && (
                    <Badge className="bg-violet-100 text-violet-700 hover:bg-violet-100 border-violet-200 text-[10px] gap-1">
                      <Mic2 className="h-3 w-3" />
                      Custom audio
                    </Badge>
                  )}
                  {o.tts_text || (!hasCustomAudio
                    ? <span className="italic text-slate-400">(empty override)</span>
                    : null)}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Reseller / office manager: inline editor
  return (
    <div className="space-y-0">
      {LANGS.map((lang) => (
        <OverrideRow
          key={lang}
          prompt={prompt}
          lang={lang}
          override={overrideByLang[lang]}
          baseline={translationByLang[lang]}
        />
      ))}
    </div>
  );
}
